/*
**  rui-toggle.js
**
**  rui-toggle component
*/
import { LitElement, html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
export class RuiToggle extends LitElement {
    static get is() {
        return 'rui-toggle';
    }
    static get properties() {
        return {
            value: { type: Object },
            labelText: { type: String },
            isDisabled: { type: Object }
        };
    }
    constructor() {
        super();
        this.value = false;
        this.labelText = '';
        this.isDisabled = false;
        this._isFocused = false;
    }
    toggleValue(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDisabled) {
            // Store new value
            this.value = !this.value;
            // Fire value change event
            let valueChangeEvent = new CustomEvent('rui-value-change', {
                detail: this.value,
                bubbles: true,
                composed: true
            });
            this.dispatchEvent(valueChangeEvent);
        }
    }
    setFocus() {
        this._isFocused = true;
        this.requestUpdate();
    }
    blurFocus() {
        this._isFocused = false;
        this.requestUpdate();
    }
    render() {
        const toggleClasses = {
            ['rui-toggle-container']: true,
            ['rui-toggle-value-' + this.value]: true,
            ['rui-toggle-container-focused']: this._isFocused,
            ['rui-toggle-container-disabled']: this.isDisabled
        };
        const labelClasses = {
            ['rui-toggle-label']: true,
            ['rui-toggle-label-disabled']: this.isDisabled
        };
        return html `
            <div class="${classMap(toggleClasses)}" @click="${this.toggleValue}">
                <input class="rui-toggle" type="checkbox" role="checkbox" ?disabled="${this.isDisabled}" .checked=${this.value}
                    @change="${this.toggleValue}" @focus="${this.setFocus}" @blur="${this.blurFocus}" aria-disabled="${this.isDisabled}">
                <span class="rui-toggle-btn"></span>
            </div>
            ${this.labelText ? html ` <label class="${classMap(labelClasses)}" @click="${this.toggleValue}">${this.labelText}</label>` : ''}
        `;
    }
    static get styles() {
        return css `
            /* Toggle container */
            
            .rui-toggle-container {
                background-color: var(--toggle-track-border, #ddd);
                border: 1px solid var(--toggle-track-border, #ddd);
                border-radius: 15px;
                box-sizing: border-box;
                cursor: pointer;
                display: inline-block;
                height: 24px;
                min-width: 50px;
                margin: 3px 0 0 0;
                position:relative;
                text-indent: -5000px;
                touch-action: none;
                vertical-align: middle;
                width: 50px;
            }
            
            .rui-toggle-container-focused {
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-primary-border, #3878ab);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            .rui-toggle-container-disabled {
                cursor: not-allowed;
            }
            
            /* Toggle input */
            
            .rui-toggle  {
                max-height: 0;
                max-width: 0;
                opacity: 0;
                touch-action: none;
            
                display: inline-flex;
                align-items: center;
                flex-wrap: wrap;
                position: relative;
            }
            
            /* Background of the toggle slide */
            
            .rui-toggle-btn:before {
                background-color: transparent;
                border-radius: 12px;
                content:"";
                display: block;
                height: 24px;
                left: -1px;
                position: absolute;
                right: 0;
                top: -1px;
                transition: .25s ease-in-out;
                width: 24px;
            }
            
            /* Toggle: round toggle element */
            
            .rui-toggle-btn:after {
                background-color: var(--toggle-thumb-bg, #fff);
                border: 1px solid var(--toggle-track-border, #ddd);
                border-radius: 12px;
                box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.1);
                box-sizing: content-box;
                content:"";
                display: block;
                height: 22px;
                left: -1px;
                position: absolute;
                top: -1px;
                transition: .25s ease-in-out;
                width: 22px;
            }
            
            @media (prefers-reduced-motion: reduce) {
                .rui-toggle-btn:before,
                .rui-toggle-btn:after {
                    transition: none;
                }
            }
            
            /* Toggle: toggle checked, background of toggle slide */
            
            .rui-toggle:checked + .rui-toggle-btn:before {
                background-color: var(--btn-primary-bg, #3878ab);
                width: 50px;
            }
            
            /* Toggle: toggle checked, round toggle element */
            
            .rui-toggle:checked + .rui-toggle-btn:after {
                background-color: var(--toggle-thumb-bg, #fff);
                border: 1px solid var(--btn-primary-bg, #3878ab);
                box-shadow: -2px 1px 4px rgba(0, 0, 0, 0.24);
                left: 25px;
                top: -1px;
            }
            
            /* Toggle: disabled, background of the toggle slide */
            
            .rui-toggle:disabled:checked + .rui-toggle-btn:before {
                opacity: 0.5;
            }
            
            /* Toggle: disabled, round toggle element */
            
            .rui-toggle:disabled+.rui-toggle-btn:after {
                border-color: transparent;
                opacity: 0.5;
            }
            
            /* Optional text label */
            .rui-toggle-label {
                cursor: pointer;
                margin-left: 3px;
            }
            
            .rui-toggle-label-disabled {
                cursor: not-allowed;
                color: var(--text-disabled, #999);
            }

            @media print {
                .rui-toggle-container {
                    background-color: #dddddd;
                    border-color: #dddddd;
                }    
            
                .rui-toggle-btn:after {
                    background-color: #ffffff;
                    border-color: #dddddd;
                }
            
                .rui-toggle:checked + .rui-toggle-btn:before {
                    background-color: #000000;
                }
                
                .rui-toggle:checked + .rui-toggle-btn:after {
                    background-color: #ffffff;
                    border-color: #000000;
                    box-shadow: none;
                }
            }
        `;
    }
}
customElements.get(RuiToggle.is) || customElements.define(RuiToggle.is, RuiToggle);
